import React, { useEffect, useState } from 'react';
import AdminSinglePage from '../AdminSinglePage/AdminSinglePage';
import { userSingle } from '../../../redux/actions/user/userSingle';
import { resetUpdateUser, resetUserSingle } from '../../../redux/slices/user.slice';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser } from '../../../redux/actions/user/updateUser';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import Loading from '../../../components/admin/Loading/Loading';
import { Box } from '@mui/material';
import InputCustom from '../../../components/admin/InputCustom/InputCustom';
import { formatPhoneNumber, getPhoneMask, stripCountryCodeFromPhone } from '../../../utils/getPhoneMask';
import { useWatch } from 'react-hook-form';

const PhoneCountryField = ({ form, citizenships, citizenshipId, activeCitizenship, countryOptions, phoneFormat }) => {
  const selectedCitizenshipId = useWatch({ control: form.control, name: 'citizenshipId' }) || citizenshipId;
  const selectedCitizenship = citizenships?.find((country) => country?.id === selectedCitizenshipId) ?? activeCitizenship;
  const selectedPhoneFormat = getPhoneMask(selectedCitizenship) || phoneFormat;

  return (
    <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: '220px 1fr', alignItems: 'start' }}>
      <Box sx={{ minWidth: 0 }}>
        <InputCustom required={true} form={form} name={'citizenshipId'} label={'Страна / код'} isSelect options={countryOptions} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <InputCustom required={true} form={form} name={'phone'} label={'Телефон'} isNumber format={selectedPhoneFormat} placeholder={selectedPhoneFormat} />
      </Box>
    </Box>
  );
};

const AdminUserSingle = () => {
  const [fields, setFields] = useState([]);
  const [citizenshipsData, setCitizenshipsData] = useState([]);

  const data = [
    [
      {
        label: 'Аватар',
        type: 'image',
        name: 'avatar',
        width: '200px',
      },
      [
        {
          label: 'Имя',
          type: 'text',
          name: 'firstName',
        },
        { label: 'Фамилия', type: 'text', name: 'lastName' },
        {
          label: 'Дата рождения',
          type: 'date',
          name: 'birthDate',
        },
        {
          type: 'radio',
          name: 'isFemale',
          label: 'Пол',
          options: [
            {
              label: 'Мужской',
              value: 'false',
            },
            {
              label: 'Женский',
              value: 'true',
            },
          ],
        },
      ],
    ],
  ];

  const onFormatData = ({ user: { gradeId, clubId, firstName, lastName, avatar, isFemale, cityId, citizenshipId, birthDate, email, phone }, clubs, grades, citizenships, cities }) => {
    setCitizenshipsData(citizenships ?? []);

    const activeCitizenship = citizenships?.find((country) => country?.id === citizenshipId) ?? citizenships?.[0];
    const phoneFormat = getPhoneMask(activeCitizenship);
    const countryOptions = citizenships?.map((country) => ({
      value: country?.id,
      label: country?.code ? `${country?.name} +${country?.code}` : country?.name,
    }));

    const formFields = [
      [
        {
          label: 'Город',
          type: 'select',
          name: 'cityId',
          options: cities?.map((city) => ({ value: city?.id, label: city?.name })),
        },
        {
          label: 'Домашний клуб',
          type: 'select',
          name: 'clubId',
          options: clubs?.map((club) => ({ value: club?.id, label: club?.name })),
        },
      ],
      [
        {
          label: 'Уровень игры',
          type: 'select',
          name: 'gradeId',
          options: grades?.map((grade) => ({ value: grade?.id, label: grade?.name })),
        },
        {
          type: 'custom',
          render: ({ form }) => {
            return <PhoneCountryField form={form} citizenships={citizenships} citizenshipId={citizenshipId} activeCitizenship={activeCitizenship} countryOptions={countryOptions} phoneFormat={phoneFormat} />;
          },
        },
      ],
      [
        {
          required: false,
          label: 'Email',
          type: 'text',
          name: 'email',
          rules: {
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Неверный формат почты',
            },
          },
        },
      ],
    ];

    setFields([...data, ...formFields]);

    return {
      clubId,
      firstName,
      lastName,
      avatar,
      isFemale: isFemale ? 'true' : 'false',
      gradeId,
      cityId,
      citizenshipId,
      birthDate,
      email,
      phone: formatPhoneNumber(phone, activeCitizenship),
    };
  };

  const deleteProps = { successText: 'Пользователь удален', confirmTitle: `Вы уверены что хотите удалить пользователя`, status: 'DELETED' };
  const blockProps = { successText: 'Пользователь заблокирован', confirmTitle: `Вы уверены что хотите заблокировать пользователя`, status: 'BLOCKED' };
  const unblockProps = { successText: 'Пользователь разблокирован', confirmTitle: `Вы уверены что хотите разблокировать пользователя`, status: 'ACTIVE' };

  const dispatch = useDispatch();
  const { id } = useParams();
  const {
    updateUser: { data: updateUserData, loading: updateUserLoading },
  } = useSelector((state) => state.user);
  const navigate = useNavigate();

  useEffect(() => {
    if (updateUserData) {
      toast.success('Пользователь обновлен');
      navigate('/admin/users');
      dispatch(resetUpdateUser());
    }
  }, [updateUserData, dispatch, navigate]);

  return (
    <>
      <AdminSinglePage
        onSubmit={(data) => {
          const activeCitizenship = citizenshipsData?.find((country) => country?.id === data?.citizenshipId);

          dispatch(
            updateUser({
              userId: id,
              ...data,
              phone: stripCountryCodeFromPhone(data?.phone, activeCitizenship),
              isFemale: data?.isFemale === 'true',
              birthDate: new Date(data?.birthDate),
            }),
          );
        }}
        reset={resetUserSingle}
        fetch={userSingle}
        sliceName={'user'}
        actionName={'userSingle'}
        onFormatData={onFormatData}
        fields={fields}
        isSave
        pathList="/admin/users"
        deleteProps={deleteProps}
        blockProps={blockProps}
        unblockProps={unblockProps}
        propName={'user'}
        type={'USER'}
      />
      {updateUserLoading && <Loading />}
    </>
  );
};

export default AdminUserSingle;
